import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { testimonials as mockTestimonials, faqs } from '../mock';
import { publicApi } from '../lib/api';

export const Testimonials = () => {
  const [items, setItems] = useState(mockTestimonials);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    publicApi.getReviews()
      .then((data) => { if (data.length) setItems(data); })
      .catch(() => {});
  }, []);

  const next = () => setIdx((idx + 1) % items.length);
  const prev = () => setIdx((idx - 1 + items.length) % items.length);
  const t = items[idx] || items[0];

  return (
    <section id="avis" className="bg-white py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F4A340]/10 text-[#E89534] text-xs font-semibold tracking-widest uppercase mb-4">
            Avis clients
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
            Ils ont <span className="text-[#E89534]">navigué</span> avec nous
          </h2>
        </div>

        <div className="relative bg-[#FAF7F2] rounded-3xl p-10 md:p-16 border border-orange-100">
          <Quote className="absolute top-8 left-8 w-16 h-16 text-[#F4A340]/20" />

          <div key={t.id} className="relative animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex gap-1 mb-6 justify-center">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#F4A340] text-[#F4A340]" />
              ))}
            </div>
            <p className="text-xl md:text-2xl text-slate-800 leading-relaxed text-center font-medium">
              “{t.comment}”
            </p>
            <div className="mt-8 flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F4A340] to-[#E89534] flex items-center justify-center text-white font-bold text-lg shadow-md">
                {t.avatar}
              </div>
              <div className="text-center">
                <div className="font-bold text-slate-900">{t.name}</div>
                <div className="text-sm text-slate-500">{t.location}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={prev} className="w-11 h-11 rounded-full bg-white hover:bg-[#F4A340] hover:text-white shadow-md flex items-center justify-center transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-2 rounded-full transition-all ${i === idx ? 'w-8 bg-[#F4A340]' : 'w-2 bg-slate-300'}`}
                />
              ))}
            </div>
            <button onClick={next} className="w-11 h-11 rounded-full bg-white hover:bg-[#F4A340] hover:text-white shadow-md flex items-center justify-center transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" className="bg-[#FAF7F2] py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F4A340]/10 text-[#E89534] text-xs font-semibold tracking-widest uppercase mb-4">
            Questions fréquentes
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
            Tout ce qu'il faut <span className="text-[#E89534]">savoir</span>
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-300 bg-white ${
                  isOpen ? 'border-[#F4A340] shadow-md' : 'border-slate-200'
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between text-left p-5 gap-4"
                >
                  <span className={`font-semibold ${isOpen ? 'text-[#E89534]' : 'text-slate-900'}`}>
                    {f.question}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    isOpen ? 'bg-[#F4A340] text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-slate-600 leading-relaxed">{f.answer}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
